// src/pages/purchase/ReporteCompras.js
// Página de reportes de compras para contador

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Form, Alert, Pagination } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import {
  getPurchaseReport,
  getPurchaseReportTerm,
  getPurchaseReportMonth,
  getPurchaseReportPending,
  getPurchaseReportOverdue,
  getPurchaseReportDueSoon,
  getPurchaseReportByMonth
} from '../../services/purchaseApi';
import { getTaxes } from '../../services/taxApi';
import './ReporteCompras.css';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const formatterCOPDecimal = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const meses = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const ReporteCompras = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Reportes
  const [reportYearly, setReportYearly] = useState({ months: [], purchases: [] });
  const [reportTerm, setReportTerm] = useState({ items: [], summary: { grand_total: 0, total_purchases: 0 } });
  const [reportMonth, setReportMonth] = useState(0);
  const [reportPending, setReportPending] = useState(0);
  const [reportOverdue, setReportOverdue] = useState(0);
  const [reportDueSoon, setReportDueSoon] = useState({ purchases: [], summary: { total_amount: 0, count: 0 } });

  // Lista de compras para tabla detallada
  const [purchasesList, setPurchasesList] = useState([]);
  const [purchasesWithTaxes, setPurchasesWithTaxes] = useState([]);
  const [showPurchasesTable, setShowPurchasesTable] = useState(false);
  const [taxDefinitions, setTaxDefinitions] = useState([]);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  const loadAllReports = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const [
        yearlyRes,
        termRes,
        monthRes,
        pendingRes,
        overdueRes,
        dueSoonRes
      ] = await Promise.all([
        getPurchaseReport(selectedYear),
        getPurchaseReportTerm(selectedYear, selectedMonth),
        getPurchaseReportMonth(selectedYear, selectedMonth),
        getPurchaseReportPending(),
        getPurchaseReportOverdue(),
        getPurchaseReportDueSoon(30)
      ]);

      if (yearlyRes.ok) setReportYearly(yearlyRes);
      if (termRes.ok) setReportTerm(termRes);
      if (monthRes.ok) setReportMonth(monthRes.total);
      if (pendingRes.ok) setReportPending(pendingRes.total);
      if (overdueRes.ok) setReportOverdue(overdueRes.total);
      if (dueSoonRes.ok) setReportDueSoon(dueSoonRes);
    } catch (error) {
      console.error('Error cargando reportes:', error);
      setErrorMsg('Error al cargar los reportes. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  const loadPurchasesList = useCallback(async (page = 1) => {
    setLoadingPurchases(true);
    try {
      // Cargar definiciones de impuestos solo la primera vez
      if (taxDefinitions.length === 0) {
        const taxesRes = await getTaxes({ scope: 'compra' });
        if (taxesRes?.ok) {
          const list = taxesRes.data?.message ?? taxesRes.data;
          setTaxDefinitions(Array.isArray(list) ? list : []);
        }
      }

      // Usar el nuevo endpoint que incluye todos los detalles
      const res = await getPurchaseReportByMonth(selectedYear, selectedMonth, page, itemsPerPage);
      if (res.ok) {
        setPurchasesWithTaxes(res.items || []);
        setPurchasesList(res.items || []); // Mantener para compatibilidad
        setTotalPurchases(res.total_rows || 0);
        setShowPurchasesTable(true);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Error cargando compras:', error);
    } finally {
      setLoadingPurchases(false);
    }
  }, [selectedYear, selectedMonth, itemsPerPage, taxDefinitions.length]);

  useEffect(() => {
    loadAllReports();
  }, [loadAllReports]);

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  const handleRefresh = () => {
    loadAllReports();
  };

  const handleViewDetails = () => {
    setCurrentPage(1);
    loadPurchasesList(1);
  };

  const handlePageChange = (page) => {
    loadPurchasesList(page);
  };

  // Función para obtener el nombre del impuesto
  const getTaxName = (taxId) => {
    const taxDef = taxDefinitions.find((t) => String(t.id_tax) === String(taxId));
    if (taxDef) {
      const desc = taxDef.tax_description || taxDef.description || '';
      const rate = taxDef.tax_rate ?? taxDef.rate;
      if (rate != null && rate !== '') {
        const rateNum = parseFloat(rate);
        const rateStr = rateNum < 1 ? `${rateNum * 100}%` : `${rateNum}%`;
        return `${desc} ${rateStr}`;
      }
      return desc;
    }
    return `Impuesto #${taxId}`;
  };

  // Función para obtener nombre del impuesto desde el objeto inline (tax_description + tax_rate del item)
  const getTaxNameFromItem = (tax) => {
    const desc = tax.tax_description || getTaxName(tax.id_tax);
    const rate = tax.tax_rate;
    if (rate != null && rate !== '') {
      const rateNum = parseFloat(rate);
      const rateStr = rateNum < 1 ? `${rateNum * 100}%` : `${rateNum}%`;
      return `${desc} ${rateStr}`;
    }
    return desc;
  };

  // Función para obtener impuestos agrupados por tipo
  const getTaxesByType = (purchase) => {
    const taxes = purchase.purchase_taxes || [];
    const taxesByType = {};

    taxes.forEach((tax) => {
      const taxName = getTaxNameFromItem(tax);
      if (!taxesByType[taxName]) {
        taxesByType[taxName] = 0;
      }
      taxesByType[taxName] += parseFloat(tax.tax_amount || 0);
    });

    return taxesByType;
  };

  // Función para exportar a CSV - carga TODOS los datos del mes
  const handleExportCSV = async () => {
    setLoading(true);
    try {
      // Cargar definiciones de impuestos si no están cargadas
      let taxDefs = taxDefinitions;
      if (taxDefs.length === 0) {
        const taxesRes = await getTaxes({ scope: 'compra' });
        if (taxesRes?.ok) {
          const list = taxesRes.data?.message ?? taxesRes.data;
          taxDefs = Array.isArray(list) ? list : [];
        }
      }

      // Cargar TODAS las compras del mes usando el nuevo endpoint
      let allPurchases = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const res = await getPurchaseReportByMonth(selectedYear, selectedMonth, page, 100); // 100 por página (máximo permitido)
        if (res.ok && res.items && res.items.length > 0) {
          allPurchases = [...allPurchases, ...res.items];
          hasMore = page < res.total_pages;
          page++;
        } else {
          hasMore = false;
        }
      }

      if (allPurchases.length === 0) {
        alert('No hay datos para exportar');
        setLoading(false);
        return;
      }

      // Los datos ya vienen con todos los detalles (impuestos incluidos)
      const purchasesWithDetails = allPurchases;

      // Función auxiliar para obtener nombre del impuesto (usa tax_description inline)
      const getTaxNameForCSV = (tax) => {
        const desc = tax.tax_description || (() => {
          const taxDef = taxDefs.find((t) => String(t.id_tax) === String(tax.id_tax));
          return taxDef ? (taxDef.tax_description || taxDef.description || '') : `Impuesto #${tax.id_tax}`;
        })();
        const rate = tax.tax_rate;
        if (rate != null && rate !== '') {
          const rateNum = parseFloat(rate);
          const rateStr = rateNum < 1 ? `${rateNum * 100}%` : `${rateNum}%`;
          return `${desc} ${rateStr}`;
        }
        return desc;
      };

      // Obtener todos los tipos de impuestos únicos
      const allTaxTypes = new Set();
      purchasesWithDetails.forEach((purchase) => {
        const taxes = purchase.purchase_taxes || [];
        taxes.forEach((tax) => {
          allTaxTypes.add(getTaxNameForCSV(tax));
        });
      });
      const taxTypesArray = Array.from(allTaxTypes).sort();

      // Función para obtener impuestos agrupados por tipo
      const getTaxesByTypeForCSV = (purchase) => {
        const taxes = purchase.purchase_taxes || [];
        const taxesByType = {};

        taxes.forEach((tax) => {
          const taxName = getTaxNameForCSV(tax);
          if (!taxesByType[taxName]) {
            taxesByType[taxName] = 0;
          }
          taxesByType[taxName] += parseFloat(tax.tax_amount || 0);
        });

        return taxesByType;
      };

      // Crear encabezados
      const headers = [
        'ID',
        'Fecha',
        'Proveedor',
        'Referencia',
        'Subtotal',
        'Descuento',
        ...taxTypesArray,
        'Total Impuestos',
        'Total'
      ];

      // Crear filas de datos
      const rows = purchasesWithDetails.map((purchase) => {
        const taxesByType = getTaxesByTypeForCSV(purchase);
        const totalTaxes = Object.values(taxesByType).reduce((sum, val) => sum + val, 0);
        
        const row = [
          purchase.id_purchase || '',
          purchase.purchase_date || '',
          purchase.supplier_name || purchase.supplier?.name || '',
          purchase.reference_doc || '',
          (purchase.subtotal || 0).toString(),
          (purchase.total_discount || 0).toString(),
          ...taxTypesArray.map((taxType) => (taxesByType[taxType] || 0).toString()),
          totalTaxes.toString(),
          (purchase.grand_total || 0).toString()
        ];
        return row;
      });

      // Agregar fila de totales
      const totals = [
        'TOTAL',
        '',
        '',
        '',
        purchasesWithDetails.reduce((sum, p) => sum + (p.subtotal || 0), 0).toString(),
        purchasesWithDetails.reduce((sum, p) => sum + (p.total_discount || 0), 0).toString(),
        ...taxTypesArray.map((taxType) => {
          return purchasesWithDetails.reduce((sum, p) => {
            const taxesByType = getTaxesByTypeForCSV(p);
            return sum + (taxesByType[taxType] || 0);
          }, 0).toString();
        }),
        purchasesWithDetails.reduce((sum, p) => {
          const taxesByType = getTaxesByTypeForCSV(p);
          return sum + Object.values(taxesByType).reduce((s, v) => s + v, 0);
        }, 0).toString(),
        purchasesWithDetails.reduce((sum, p) => sum + (p.grand_total || 0), 0).toString()
      ];
      rows.push(totals);

      // Convertir a CSV
      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Crear blob y descargar
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `reporte-compras-${meses[selectedMonth - 1]}-${selectedYear}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exportando CSV:', error);
      alert('Error al exportar el CSV. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Calcular máximo para gráfico
  const maxPurchase = Math.max(...reportYearly.purchases.map(v => parseFloat(v) || 0), 0);
  const maxHeight = 200; // altura máxima del gráfico en px

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Container fluid className="reporte-compras-container">
        <div className="reporte-compras-header">
          <div className="reporte-compras-title-section">
            <h1 className="reporte-compras-title">
              <span className="title-icon">📊</span>
              Reportes de Compras
            </h1>
            <p className="reporte-compras-subtitle">Análisis financiero y contable de compras</p>
          </div>
          <div className="reporte-compras-controls">
            <Form.Select
              value={selectedYear}
              onChange={handleYearChange}
              className="reporte-compras-select"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - i).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              value={selectedMonth}
              onChange={handleMonthChange}
              className="reporte-compras-select"
            >
              {meses.map((mes, index) => (
                <option key={index + 1} value={index + 1}>
                  {mes}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="primary"
              onClick={handleRefresh}
              disabled={loading}
              className="reporte-compras-btn-refresh"
            >
              {loading ? <Spinner animation="border" size="sm" /> : '🔄 Actualizar'}
            </Button>
          </div>
        </div>

        {errorMsg && (
          <Alert variant="danger" className="reporte-compras-alert">
            {errorMsg}
          </Alert>
        )}

        {/* Cards de resumen */}
        <Row className="reporte-compras-summary-row">
          <Col xs={12} md={6} lg={3}>
            <Card className="reporte-compras-summary-card reporte-compras-card-primary">
              <Card.Body>
                <div className="reporte-compras-card-icon">💰</div>
                <div className="reporte-compras-card-content">
                  <h6 className="reporte-compras-card-label">Total del Mes</h6>
                  <h3 className="reporte-compras-card-value">{formatterCOP.format(parseFloat(reportMonth) || 0)}</h3>
                  <p className="reporte-compras-card-period">{meses[selectedMonth - 1]} {selectedYear}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Card className="reporte-compras-summary-card reporte-compras-card-warning">
              <Card.Body>
                <div className="reporte-compras-card-icon">⏳</div>
                <div className="reporte-compras-card-content">
                  <h6 className="reporte-compras-card-label">Pendientes</h6>
                  <h3 className="reporte-compras-card-value">{formatterCOP.format(parseFloat(reportPending) || 0)}</h3>
                  <p className="reporte-compras-card-period">Por pagar</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Card className="reporte-compras-summary-card reporte-compras-card-danger">
              <Card.Body>
                <div className="reporte-compras-card-icon">⚠️</div>
                <div className="reporte-compras-card-content">
                  <h6 className="reporte-compras-card-label">Vencidas</h6>
                  <h3 className="reporte-compras-card-value">{formatterCOP.format(parseFloat(reportOverdue) || 0)}</h3>
                  <p className="reporte-compras-card-period">Requieren atención</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Card className="reporte-compras-summary-card reporte-compras-card-info">
              <Card.Body>
                <div className="reporte-compras-card-icon">📅</div>
                <div className="reporte-compras-card-content">
                  <h6 className="reporte-compras-card-label">Por Vencer</h6>
                  <h3 className="reporte-compras-card-value">{formatterCOP.format(parseFloat(reportDueSoon.summary.total_amount) || 0)}</h3>
                  <p className="reporte-compras-card-period">{reportDueSoon.summary.count} compras (30 días)</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Gráfico anual */}
        <Row className="reporte-compras-chart-row">
          <Col xs={12}>
            <Card className="reporte-compras-chart-card">
              <Card.Header className="reporte-compras-card-header">
                <h5 className="reporte-compras-card-title">
                  <span className="card-title-icon">📈</span>
                  Compras por Mes - {selectedYear}
                </h5>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="reporte-compras-loading">
                    <Spinner animation="border" />
                  </div>
                ) : reportYearly.purchases.length > 0 ? (
                  <div className="reporte-compras-chart">
                    {reportYearly.months.map((month, index) => {
                      const value = parseFloat(reportYearly.purchases[index]) || 0;
                      const height = maxPurchase > 0 ? (value / maxPurchase) * maxHeight : 0;
                      return (
                        <div key={index} className="reporte-compras-chart-bar-container">
                          <div className="reporte-compras-chart-bar-wrapper">
                            <div
                              className="reporte-compras-chart-bar"
                              style={{ height: `${height}px` }}
                              title={`${month}: ${formatterCOP.format(value)}`}
                            >
                              <span className="reporte-compras-chart-value">{formatterCOP.format(value)}</span>
                            </div>
                          </div>
                          <div className="reporte-compras-chart-label">{month.substring(0, 3)}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="reporte-compras-empty">No hay datos disponibles para el año seleccionado.</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Reporte por términos */}
        <Row className="reporte-compras-term-row">
          <Col xs={12} lg={6}>
            <Card className="reporte-compras-term-card">
              <Card.Header className="reporte-compras-card-header">
                <h5 className="reporte-compras-card-title">
                  <span className="card-title-icon">⏱️</span>
                  Compras por Términos de Crédito
                </h5>
                <span className="reporte-compras-card-subtitle">{meses[selectedMonth - 1]} {selectedYear}</span>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="reporte-compras-loading">
                    <Spinner animation="border" />
                  </div>
                ) : reportTerm.items.length > 0 ? (
                  <>
                    <Table responsive className="reporte-compras-term-table">
                      <thead>
                        <tr>
                          <th>Término</th>
                          <th className="text-end">Cantidad</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportTerm.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.terms_name || item.term_name || '-'}</td>
                            <td className="text-end">{item.total_purchases || 0}</td>
                            <td className="text-end">{formatterCOP.format(parseFloat(item.grand_total) || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="reporte-compras-term-total">
                          <td><strong>Total</strong></td>
                          <td className="text-end"><strong>{reportTerm.summary.total_purchases || 0}</strong></td>
                          <td className="text-end"><strong>{formatterCOP.format(parseFloat(reportTerm.summary.grand_total) || 0)}</strong></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </>
                ) : (
                  <p className="reporte-compras-empty">No hay compras registradas para este período.</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Compras por vencer */}
          <Col xs={12} lg={6}>
            <Card className="reporte-compras-due-soon-card">
              <Card.Header className="reporte-compras-card-header">
                <h5 className="reporte-compras-card-title">
                  <span className="card-title-icon">🔔</span>
                  Compras por Vencer (30 días)
                </h5>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="reporte-compras-loading">
                    <Spinner animation="border" />
                  </div>
                ) : reportDueSoon.purchases.length > 0 ? (
                  <div className="reporte-compras-due-soon-list">
                    {reportDueSoon.purchases.slice(0, 10).map((purchase, index) => (
                      <div key={purchase.id_purchase || index} className="reporte-compras-due-soon-item">
                        <div className="reporte-compras-due-soon-info">
                          <div className="reporte-compras-due-soon-id">#{purchase.id_purchase}</div>
                          <div className="reporte-compras-due-soon-supplier">{purchase.supplier_name || '-'}</div>
                          <div className="reporte-compras-due-soon-date">Vence: {purchase.due_date || '-'}</div>
                        </div>
                        <div className="reporte-compras-due-soon-amount">
                          {formatterCOP.format(parseFloat(purchase.grand_total) || 0)}
                        </div>
                      </div>
                    ))}
                    {reportDueSoon.purchases.length > 10 && (
                      <div className="reporte-compras-due-soon-more">
                        +{reportDueSoon.purchases.length - 10} compras más
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="reporte-compras-empty">No hay compras por vencer en los próximos 30 días.</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla detallada de compras */}
        {showPurchasesTable && (
          <Row className="reporte-compras-detail-row">
            <Col xs={12}>
              <Card className="reporte-compras-detail-card">
                <Card.Header className="reporte-compras-card-header">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 className="reporte-compras-card-title">
                      <span className="card-title-icon">📋</span>
                      Detalle de Compras - {meses[selectedMonth - 1]} {selectedYear}
                    </h5>
                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleExportCSV}
                        disabled={loading}
                        className="reporte-compras-btn-export"
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Generando...
                          </>
                        ) : (
                          '📥 Descargar CSV'
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowPurchasesTable(false)}
                      >
                        ✕ Cerrar
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  {loadingPurchases ? (
                    <div className="reporte-compras-loading">
                      <Spinner animation="border" />
                      <p className="mt-2">Cargando compras...</p>
                    </div>
                  ) : purchasesWithTaxes.length > 0 ? (
                    <>
                      <div className="reporte-compras-table-wrapper">
                        <Table responsive hover className="reporte-compras-detail-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Fecha</th>
                            <th>Proveedor</th>
                            <th>Referencia</th>
                            <th className="text-end">Subtotal</th>
                            <th className="text-end">Descuento</th>
                            {(() => {
                              // Obtener todos los tipos de impuestos únicos
                              const allTaxTypes = new Set();
                              purchasesWithTaxes.forEach((purchase) => {
                                (purchase.purchase_taxes || []).forEach((tax) => {
                                  allTaxTypes.add(getTaxNameFromItem(tax));
                                });
                              });
                              return Array.from(allTaxTypes).sort().map((taxType) => (
                                <th key={taxType} className="text-end">{taxType}</th>
                              ));
                            })()}
                            <th className="text-end">Total Impuestos</th>
                            <th className="text-end">Total</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchasesWithTaxes.map((purchase) => {
                            const taxesByType = getTaxesByType(purchase);
                            const allTaxTypes = new Set();
                            purchasesWithTaxes.forEach((p) => {
                              (p.purchase_taxes || []).forEach((tax) => {
                                allTaxTypes.add(getTaxNameFromItem(tax));
                              });
                            });
                            const taxTypesArray = Array.from(allTaxTypes).sort();
                            const totalTaxes = Object.values(taxesByType).reduce((sum, val) => sum + val, 0);
                            
                            return (
                              <tr key={purchase.id_purchase}>
                                <td>{purchase.id_purchase}</td>
                                <td>{purchase.purchase_date || '-'}</td>
                                <td>{purchase.supplier_name || purchase.supplier?.name || '-'}</td>
                                <td>{purchase.reference_doc || '-'}</td>
                                <td className="text-end">{formatterCOP.format(parseFloat(purchase.subtotal) || 0)}</td>
                                <td className="text-end">{formatterCOP.format(parseFloat(purchase.total_discount) || 0)}</td>
                                {taxTypesArray.map((taxType) => (
                                  <td key={taxType} className="text-end">
                                    {formatterCOP.format(parseFloat(taxesByType[taxType]) || 0)}
                                  </td>
                                ))}
                                <td className="text-end">{formatterCOP.format(parseFloat(totalTaxes) || 0)}</td>
                                <td className="text-end"><strong>{formatterCOP.format(parseFloat(purchase.grand_total) || 0)}</strong></td>
                                <td>
                                  <span className={`reporte-compras-status-badge ${(purchase.status || '').toLowerCase().replace('_', '-')}`}>
                                    {purchase.status || 'Pendiente'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="reporte-compras-detail-total">
                            <td colSpan="4"><strong>Total</strong></td>
                            <td className="text-end">
                              <strong>{formatterCOP.format(purchasesWithTaxes.reduce((sum, p) => sum + (parseFloat(p.subtotal) || 0), 0))}</strong>
                            </td>
                            <td className="text-end">
                              <strong>{formatterCOP.format(purchasesWithTaxes.reduce((sum, p) => sum + (parseFloat(p.total_discount) || 0), 0))}</strong>
                            </td>
                            {(() => {
                              const allTaxTypes = new Set();
                              purchasesWithTaxes.forEach((purchase) => {
                                (purchase.purchase_taxes || []).forEach((tax) => {
                                  allTaxTypes.add(getTaxNameFromItem(tax));
                                });
                              });
                              return Array.from(allTaxTypes).sort().map((taxType) => {
                                const total = purchasesWithTaxes.reduce((sum, p) => {
                                  const taxesByType = getTaxesByType(p);
                                  return sum + (taxesByType[taxType] || 0);
                                }, 0);
                                return (
                                  <td key={taxType} className="text-end">
                                    <strong>{formatterCOP.format(total)}</strong>
                                  </td>
                                );
                              });
                            })()}
                            <td className="text-end">
                              <strong>
                                {formatterCOP.format(
                                  purchasesWithTaxes.reduce((sum, p) => {
                                    const taxesByType = getTaxesByType(p);
                                    return sum + Object.values(taxesByType).reduce((s, v) => s + v, 0);
                                  }, 0)
                                )}
                              </strong>
                            </td>
                            <td className="text-end">
                              <strong>{formatterCOP.format(purchasesWithTaxes.reduce((sum, p) => sum + (parseFloat(p.grand_total) || 0), 0))}</strong>
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                    {totalPurchases > itemsPerPage && (
                      <div className="reporte-compras-pagination-wrapper">
                        <div className="reporte-compras-pagination-info">
                          Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalPurchases)} de {totalPurchases} compras
                        </div>
                        <Pagination className="reporte-compras-pagination">
                          <Pagination.First
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(1)}
                          />
                          <Pagination.Prev
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                          />
                          {Array.from({ length: Math.ceil(totalPurchases / itemsPerPage) }, (_, i) => i + 1)
                            .filter((page) => {
                              // Mostrar primera página, última página, página actual y páginas adyacentes
                              return (
                                page === 1 ||
                                page === Math.ceil(totalPurchases / itemsPerPage) ||
                                Math.abs(page - currentPage) <= 2
                              );
                            })
                            .map((page, index, array) => {
                              // Agregar ellipsis si hay gap
                              const prevPage = array[index - 1];
                              const showEllipsis = prevPage && page - prevPage > 1;
                              
                              return (
                                <React.Fragment key={page}>
                                  {showEllipsis && <Pagination.Ellipsis disabled />}
                                  <Pagination.Item
                                    active={page === currentPage}
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </Pagination.Item>
                                </React.Fragment>
                              );
                            })}
                          <Pagination.Next
                            disabled={currentPage >= Math.ceil(totalPurchases / itemsPerPage)}
                            onClick={() => handlePageChange(currentPage + 1)}
                          />
                          <Pagination.Last
                            disabled={currentPage >= Math.ceil(totalPurchases / itemsPerPage)}
                            onClick={() => handlePageChange(Math.ceil(totalPurchases / itemsPerPage))}
                          />
                        </Pagination>
                      </div>
                    )}
                  </>
                  ) : (
                    <p className="reporte-compras-empty">No hay compras registradas para este período.</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Botón para ver detalle */}
        {!showPurchasesTable && (
          <Row>
            <Col xs={12} className="text-center">
              <Button
                variant="outline-primary"
                size="lg"
                onClick={handleViewDetails}
                disabled={loading}
                className="reporte-compras-btn-details"
              >
                📋 Ver Detalle de Compras del Mes
              </Button>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

export default ReporteCompras;
